import {
  resolveSkipColumn,
  skipCellValuesMatch,
} from "./mappingRulesUtils";
import {
  CELL_MATCH_MODE_CONTAINS,
  CELL_MATCH_MODE_EQUALS,
  CELL_MATCH_MODE_NOT_CONTAINS,
  CELL_MATCH_MODE_NOT_EQUALS,
  formatMatchModeLabel,
  normalizeCellMatchMode,
  valuesMatchByMode,
} from "./cellMatchModeUtils";

export const PRODUCT_COLUMN_LABELS = {
  product_list_id: "product_list_id",
  nome: "nome",
  marca: "marca",
  codice_prodotto: "codice_prodotto",
  peso: "peso",
};

export const CATALOG_DB_COLUMN_LABELS = {
  catalog_list_id: "catalog_list_id",
  codice_interno: "codice_interno",
  disponibilita: "disponibilita",
  codice_ean: "codice_ean",
  descrizione: "descrizione",
  prezzo_di_riferimento: "prezzo_di_riferimento",
  prezzo_vendita: "prezzo_vendita",
  spese_di_spedizione: "spese_di_spedizione",
  link_offerta: "link_offerta",
  link_all_offerta: "link_all_offerta",
  link_immagine: "link_immagine",
  link_immagine_1: "link_immagine_1",
  link_immagine_2: "link_immagine_2",
  created_at: "created_at",
  vendor_id: "vendor_id",
  category_list_id: "category_list_id",
  albero_categorie: "albero_categorie",
  id_product: "id_product",
  product_list_id: "product_list_id",
  is_deleted: "is_deleted",
  search_vector: "search_vector",
  isdisattivato: "isdisattivato",
  certificato: "certificato",
  certificatore: "certificatore",
  datacert: "datacert",
  percert: "percert",
  stato: "stato",
  note: "note",
};

const IMPORT_WARNING_REASONS = new Set(["immagine mancante"]);

export const IMPORT_FIELD_ISSUE_MESSAGES = {
  name: "titolo mancante",
  image_path: "immagine mancante",
  price: "prezzo mancante o uguale a 0",
  _row: "Errore riga",
};

export const IMPORT_REASON_TO_FIELDS = {
  "identificatore mancante": ["internal_code", "ean_code"],
  "codice prodotto non generabile": ["internal_code", "ean_code"],
  "identificatore incoerente con codice prodotto": ["internal_code", "ean_code"],
  "match identita ambiguo": ["internal_code", "ean_code"],
  "inserimento non consentito su catalogo esistente": ["internal_code", "ean_code"],
  "product_url mancante": ["product_url"],
  "product_url non valido": ["product_url"],
  "Field internal_code duplicato nel feed": ["internal_code"],
};

export const IMPORT_REASON_DISPLAY = {
  "identificatore mancante": "Manca almeno un identificatore prodotto nel feed tra internal_code o ean_code",
  "codice prodotto non generabile": "internal_code o ean_code mancante o non valido",
  "identificatore incoerente con codice prodotto": "Il codice prodotto in DB non corrisponde agli identificatori del feed",
  "match identita ambiguo": "Identificatore feed associato a piu cataloghi attivi",
  "inserimento non consentito su catalogo esistente": "Nuovo prodotto non inseribile perche il vendor ha gia cataloghi attivi",
  "product_url mancante": "URL prodotto (product_url) mancante nel feed",
  "product_url non valido": "URL prodotto (product_url) non valido: serve un indirizzo http o https con host",
  "Field internal_code duplicato nel feed": "Field internal_code duplicato nel feed",
};

const IMPORT_FIELD_ISSUE_CODES = new Set(Object.keys(IMPORT_FIELD_ISSUE_MESSAGES));

const mapIssueCodeToMessage = (code) => (
  IMPORT_FIELD_ISSUE_MESSAGES[code]
  || IMPORT_REASON_DISPLAY[code]
  || String(code)
);

const issueTargetsMappedField = (issues, mappedField) => {
  if (!issues || !mappedField) {
    return false;
  }
  if (Array.isArray(issues)) {
    return issues.some((issueCode) => {
      const fields = IMPORT_REASON_TO_FIELDS[issueCode] || [issueCode];
      return fields.includes(mappedField);
    });
  }
  if (issues[mappedField] != null && String(issues[mappedField]).trim() !== "") {
    return true;
  }
  return Object.entries(issues).some(([issueCode]) => {
    const fields = IMPORT_REASON_TO_FIELDS[issueCode] || [issueCode];
    return fields.includes(mappedField);
  });
};

const formatFieldIssueEntries = (fieldIssueMap) => (
  Object.entries(fieldIssueMap).map(([field, reason]) => {
    if (IMPORT_REASON_DISPLAY[field] || IMPORT_REASON_TO_FIELDS[field]) {
      return mapIssueCodeToMessage(field);
    }
    return `${field}: ${reason}`;
  }).join(" | ")
);

const codesToFieldIssueMap = (codes) => {
  if (!Array.isArray(codes) || codes.length === 0) {
    return null;
  }
  const resolved = {};
  codes.forEach((code) => {
    const normalized = String(code).trim();
    if (normalized) {
      resolved[normalized] = mapIssueCodeToMessage(normalized);
    }
  });
  return Object.keys(resolved).length > 0 ? resolved : null;
};

const expandRowProblemEntry = (entry) => {
  if (!entry || typeof entry !== "object") {
    return { errors: null, warnings: null };
  }
  if (Array.isArray(entry.errors) || Array.isArray(entry.warnings)) {
    return {
      errors: codesToFieldIssueMap(entry.errors),
      warnings: codesToFieldIssueMap(entry.warnings),
    };
  }
  return splitLegacyFieldIssues(entry);
};

const resolveRowProblems = (importData) => {
  const explicit = importData?.row_problems;
  if (explicit && typeof explicit === "object" && Object.keys(explicit).length > 0) {
    return explicit;
  }
  const legacyErrors = importData?.row_errors || {};
  const legacyWarnings = importData?.row_warnings || {};
  const merged = {};
  const keys = new Set([...Object.keys(legacyErrors), ...Object.keys(legacyWarnings)]);
  keys.forEach((key) => {
    const splitErrors = splitLegacyFieldIssues(legacyErrors[key]);
    const splitWarnings = splitLegacyFieldIssues(legacyWarnings[key]);
    const entry = {};
    if (splitErrors.errors) {
      entry.errors = Object.keys(splitErrors.errors);
    }
    if (splitWarnings.warnings || splitErrors.warnings) {
      entry.warnings = Object.keys({ ...(splitWarnings.warnings || {}), ...(splitErrors.warnings || {}) });
    }
    if (entry.errors?.length || entry.warnings?.length) {
      merged[key] = entry;
    }
  });
  return merged;
};

export const productsImportedCount = (importData) => Number(importData?.products_imported || 0);

export const runSummaryStatus = (summary) => String(summary?.status || "").trim();

export const runSummaryTs = (summary) => String(summary?.ts || "").trim();

export const runSummaryTrigger = (summary) => String(summary?.trigger || summary?.import_type || "").trim();

export const formatCatalogCell = (column, value) => {
  if (value == null || value === "") {
    return "—";
  }
  if (column === "description" || column === "descrizione") {
    const text = String(value);
    return text.length > 120 ? `${text.slice(0, 120)}...` : text;
  }
  if (column === "field_errors" && value) {
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return formatFieldIssueEntries(parsed);
      }
    } catch {
      return String(value);
    }
  }
  if (typeof value === "boolean") {
    return value ? "Sì" : "No";
  }
  if (column === "certificato") {
    if (value === 1 || value === "1") {
      return "Sì";
    }
    if (value === 0 || value === "0") {
      return "No";
    }
  }
  if (column === "stato") {
    const statoLabels = {
      1: "Da categorizzare",
      2: "In lavorazione",
      3: "Categorizzato",
    };
    return statoLabels[value] || String(value);
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toLocaleString("it-IT");
  }
  return String(value);
};

const normalizeFieldIssueMap = (fieldMap) => {
  if (!fieldMap || typeof fieldMap !== "object") {
    return null;
  }
  const entries = Object.entries(fieldMap).filter(([, reason]) => reason != null && String(reason).trim() !== "");
  return entries.length > 0 ? Object.fromEntries(entries) : null;
};

const splitLegacyFieldIssues = (fieldMap) => {
  const normalized = normalizeFieldIssueMap(fieldMap);
  if (!normalized) {
    return { errors: null, warnings: null };
  }
  const errors = {};
  const warnings = {};
  Object.entries(normalized).forEach(([field, reason]) => {
    const text = String(reason).trim();
    if (IMPORT_WARNING_REASONS.has(text)) {
      warnings[field] = text;
    } else {
      errors[field] = text;
    }
  });
  return {
    errors: Object.keys(errors).length > 0 ? errors : null,
    warnings: Object.keys(warnings).length > 0 ? warnings : null,
  };
};

const resolveGlobalFieldIssues = (importData, { warningsOnly = false } = {}) => {
  const imported = Number(importData?.products_imported || 0);
  const totalProducts = Number(importData?.total_products || 0);
  const withErrors = Number(importData?.products_with_errors || 0);
  const withWarnings = Number(importData?.products_with_warnings || 0);
  const issueCount = warningsOnly ? withWarnings : withErrors;
  if (!importData || imported <= 0 || issueCount !== imported) {
    return null;
  }
  // Incremental imports: only error rows are written; do not paint the whole feed.
  if (totalProducts > imported) {
    return null;
  }
  const rowProblems = resolveRowProblems(importData);
  if (Object.keys(rowProblems).length > 0 && Object.keys(rowProblems).length < totalProducts) {
    return null;
  }

  const globalItems = warningsOnly
    ? (importData.global_warnings || [])
    : (importData.global_errors || []);

  const globalReasons = globalItems
    .map((item) => {
      const text = String(item || "").trim();
      if (!text) {
        return "";
      }
      if (IMPORT_FIELD_ISSUE_CODES.has(text)) {
        return mapIssueCodeToMessage(text);
      }
      return text.startsWith("Tutti i record: ") ? text.slice("Tutti i record: ".length).trim() : text;
    })
    .filter(Boolean);

  if (!globalReasons.length) {
    return null;
  }

  const resolved = {};
  globalReasons.forEach((reason) => {
    if (warningsOnly && !IMPORT_WARNING_REASONS.has(reason)) {
      return;
    }
    if (!warningsOnly && IMPORT_WARNING_REASONS.has(reason)) {
      return;
    }
    if (reason === "titolo mancante") {
      resolved.name = reason;
    } else if (reason === "immagine mancante") {
      resolved.image_path = reason;
    } else if (reason === "prezzo mancante o uguale a 0") {
      resolved.price = reason;
    } else {
      resolved._row = reason;
    }
  });
  return Object.keys(resolved).length > 0 ? resolved : null;
};

export const getFeedRowFieldErrors = (rowErrors, feedRow, importData = null) => {
  if (feedRow == null) {
    return null;
  }

  const key = String(feedRow);
  const rowProblems = resolveRowProblems(importData);
  const problemEntry = expandRowProblemEntry(rowProblems[key]);
  if (problemEntry.errors) {
    return problemEntry.errors;
  }

  const split = splitLegacyFieldIssues(rowErrors?.[key]);
  if (split.errors) {
    return split.errors;
  }

  const reclassifiedErrors = normalizeFieldIssueMap(rowErrors?.[key]);
  if (reclassifiedErrors && !split.warnings) {
    return reclassifiedErrors;
  }

  return resolveGlobalFieldIssues(importData, { warningsOnly: false });
};

export const getFeedRowFieldWarnings = (rowWarnings, feedRow, importData = null, rowErrors = null) => {
  if (feedRow == null) {
    return null;
  }

  const key = String(feedRow);
  const rowProblems = resolveRowProblems(importData);
  const problemEntry = expandRowProblemEntry(rowProblems[key]);
  if (problemEntry.warnings) {
    return problemEntry.warnings;
  }

  const explicit = normalizeFieldIssueMap(rowWarnings?.[key]);
  if (explicit) {
    return explicit;
  }

  const legacySource = rowErrors?.[key] || importData?.row_errors?.[key];
  const legacySplit = splitLegacyFieldIssues(legacySource);
  if (legacySplit.warnings) {
    return legacySplit.warnings;
  }

  return resolveGlobalFieldIssues(importData, { warningsOnly: true });
};

export const getFeedRowImportIssues = (rowErrors, rowWarnings, feedRow, importData = null) => ({
  errors: getFeedRowFieldErrors(rowErrors, feedRow, importData),
  warnings: getFeedRowFieldWarnings(rowWarnings, feedRow, importData, rowErrors),
});

export const formatFeedRowErrors = (rowErrors, feedRow, importData = null) => {
  const { errors, warnings } = getFeedRowImportIssues(
    rowErrors,
    importData?.row_warnings,
    feedRow,
    importData,
  );
  const parts = [];
  if (errors) {
    parts.push(formatCatalogCell("field_errors", errors));
  }
  if (warnings) {
    parts.push(formatCatalogCell("field_errors", warnings));
  }
  return parts.join(" | ");
};

export const feedColumnHasImportError = (fieldErrors, column, mapping, skipValue = "skip") => {
  if (!fieldErrors) {
    return false;
  }
  const mappedField = mapping?.[column];
  if (!mappedField || mappedField === skipValue) {
    return false;
  }
  return issueTargetsMappedField(fieldErrors, mappedField);
};

export const feedColumnHasImportWarning = (fieldWarnings, column, mapping, skipValue = "skip") => {
  if (!fieldWarnings) {
    return false;
  }
  const mappedField = mapping?.[column];
  if (!mappedField || mappedField === skipValue) {
    return false;
  }
  if (Array.isArray(fieldWarnings)) {
    return fieldWarnings.includes(mappedField);
  }
  const reason = fieldWarnings[mappedField];
  return reason != null && String(reason).trim() !== "";
};

export const buildImportCellStateClasses = ({ hasError = false, hasWarning = false, hasSkip = false }) => {
  const classes = ["cm-editable-cell"];

  if (hasSkip && hasError) {
    classes.push("cm-cell-import-error-skip");
  } else if (hasSkip && hasWarning) {
    classes.push("cm-cell-import-warning-skip");
  } else if (hasError && hasWarning) {
    classes.push("cm-cell-import-error-warning");
  } else if (hasError) {
    classes.push("cm-cell-import-error");
  } else if (hasWarning) {
    classes.push("cm-cell-import-warning");
  } else if (hasSkip) {
    classes.push("cm-cell-skip-rule");
  }

  return classes.join(" ");
};

export const buildImportIssuesColumnClass = ({ hasError = false, hasWarning = false }) => {
  const classes = ["cm-mapping-error-col"];
  if (hasError) {
    classes.push("cm-cell-import-error");
    if (hasWarning) {
      classes.push("cm-cell-import-error-warning");
    }
  } else if (hasWarning) {
    classes.push("cm-cell-import-warning");
  }
  return classes.join(" ");
};

export const feedColumnMatchesSkipRule = (row, column, rules, headerLabels = null) => {
  if (!row || !rules?.length) {
    return false;
  }
  return rules.some((rule) => {
    const ruleColumn = resolveSkipColumn(rule.column, headerLabels);
    return String(ruleColumn) === String(column)
      && skipCellValuesMatch(row[column], rule.value, rule.mode);
  });
};

export const feedRowMatchesSkipRules = (row, rules, headerLabels = null) => {
  if (!row || !rules?.length) {
    return false;
  }
  return rules.some((rule) => feedColumnMatchesSkipRule(row, rule.column, [rule], headerLabels));
};

export const catalogRowMatchesBulkRules = (row, rules) => {
  if (!row || !rules?.length) {
    return false;
  }
  const normalized = String(row?.catalog?.albero_categorie || "").trim();
  return rules.some((rule) => rule.alberoValue === normalized);
};

const HARD_DELETE_INTEGER_COLUMNS = new Set([
  "disponibilita",
  "is_deleted",
  "isdisattivato",
  "stato",
  "category_list_id",
  "product_list_id",
  "catalog_list_id",
  "vendor_id",
  "product_list_id",
]);

const HARD_DELETE_FLOAT_COLUMNS = new Set([
  "prezzo_di_riferimento",
  "prezzo_vendita",
  "spese_di_spedizione",
  "peso",
]);

export const valuesMatchForHardDelete = (cellValue, ruleValue, column, mode = CELL_MATCH_MODE_EQUALS) => {
  const normalizedRule = String(ruleValue ?? "").trim();
  const normalizedMode = normalizeCellMatchMode(mode);
  let equalsMatch = false;

  if (column === "certificato") {
    if (normalizedRule === "") {
      equalsMatch = cellValue == null;
    } else {
      const lowered = normalizedRule.toLowerCase();
      if (["1", "true", "si", "sì", "yes"].includes(lowered)) {
        equalsMatch = cellValue === true;
      } else if (["0", "false", "no"].includes(lowered)) {
        equalsMatch = cellValue === false;
      } else {
        equalsMatch = false;
      }
    }
  } else if (HARD_DELETE_INTEGER_COLUMNS.has(column)) {
    if (normalizedRule === "") {
      equalsMatch = cellValue == null || String(cellValue).trim() === "";
    } else {
      const parsed = Number.parseInt(normalizedRule, 10);
      equalsMatch = !Number.isNaN(parsed) && Number(cellValue) === parsed;
    }
  } else if (HARD_DELETE_FLOAT_COLUMNS.has(column)) {
    if (normalizedRule === "") {
      equalsMatch = cellValue == null || String(cellValue).trim() === "";
    } else {
      const parsedCell = Number(String(cellValue ?? "").replace(",", "."));
      const parsedRule = Number(normalizedRule.replace(",", "."));
      equalsMatch = !Number.isNaN(parsedCell) && !Number.isNaN(parsedRule) && parsedCell === parsedRule;
    }
  } else {
    return valuesMatchByMode(cellValue, normalizedRule, normalizedMode);
  }

  if (normalizedMode === CELL_MATCH_MODE_CONTAINS) {
    if (normalizedRule === "") {
      return equalsMatch;
    }
    return String(cellValue ?? "").toLowerCase().includes(normalizedRule.toLowerCase());
  }
  if (normalizedMode === CELL_MATCH_MODE_NOT_EQUALS) {
    return !equalsMatch;
  }
  if (normalizedMode === CELL_MATCH_MODE_NOT_CONTAINS) {
    if (normalizedRule === "") {
      return !equalsMatch;
    }
    return !String(cellValue ?? "").toLowerCase().includes(normalizedRule.toLowerCase());
  }
  return equalsMatch;
};

export const catalogRowMatchesHardDeleteRule = (row, rule) => {
  if (!row || !rule) {
    return false;
  }
  const source = rule.table === "product" ? row.product : row.catalog;
  if (!source) {
    return false;
  }
  return valuesMatchForHardDelete(source[rule.column], rule.value, rule.column, rule.mode);
};

export const catalogRowMatchesHardDeleteRules = (row, rules) => {
  if (!row || !rules?.length) {
    return false;
  }
  return rules.every((rule) => catalogRowMatchesHardDeleteRule(row, rule));
};

export const formatHardDeleteRulesLabel = (rules) => {
  if (!rules?.length) {
    return "";
  }
  return rules.map((rule) => formatHardDeleteRuleLabel(rule)).join(" AND ");
};

export const formatHardDeleteRuleLabel = (rule) => {
  if (!rule) {
    return "";
  }
  const tableLabel = rule.table === "product" ? "products" : "catalogs";
  const columnLabel = rule.table === "product"
    ? (PRODUCT_COLUMN_LABELS[rule.column] || rule.column)
    : (CATALOG_DB_COLUMN_LABELS[rule.column] || rule.column);
  const modeLabel = formatMatchModeLabel(rule.mode);
  const valueLabel = String(rule.value ?? "").trim() === "" ? "(vuoto/null)" : `"${rule.value}"`;
  return `${tableLabel}.${columnLabel} ${modeLabel} ${valueLabel}`;
};
